import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Form, message } from 'antd';
import { useTranslation } from 'react-i18next';
import debounce from 'lodash/debounce';
import map from 'lodash/map';
import { getBlocks, getGenders, getServices, getProjects, getProject } from '../../../services/general';
import { overcapacityCheck } from '../../../services/reservation';
import { getCookie } from '../../../services/cookies';
import usePatient from './usePatient';
import useProfessionField from './useProfessionField';
import evaluationTypes from '../../../utils/evaluationType.json';
import { evaluateConditionals } from '../../../services/admission';
import { PRESTATIONS_SEARCH_DEBOUNCE_MS, hasEnoughSearchLength } from '../../../utils/searchConfig';

export const usePatientFormPE = ({
  index,
  patient,
  patients,
  reservationForm,
  updatePatientAttribute,
  updatePatientServices,
  repeatBlocksForAllPatients,
  setDisabled,
  countrySettings,
  jobPositionsList,
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [overCapacityForm] = Form.useForm();

  const [loading, setLoading] = useState(false);
  const [overCapacityLoading, setOverCapacityLoading] = useState(false);
  const [loadingServices, setLoadingServices] = useState(false);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [loadingPatient, setLoadingPatient] = useState({
    evaluationType: false,
  })
  const [projectsList, setProjectsList] = useState([]);
  const [isOverCapacityModalVisible, setIsOverCapacityModalVisible] = useState();
  const [currentBlock, setCurrentBlock] = useState();
  const [services, setServices] = useState([]);
  const [blocks, setBlocks] = useState();
  const [extraServiceData, setExtraServiceData] = useState([]);
  const [documentType, setDocumentType] = useState('dni');
  const [genders, setGenders] = useState([]);

  const searchPatient = usePatient(form, updatePatientAttribute, index, setDisabled, patients);
  const { isProfessionEditable, currentEvaluationType } = useProfessionField(form, "PE", evaluationTypes);

  useEffect(() => {
    getGenders().then(setGenders).catch(error => console.error('Error fetching genders:', error));
  }, []);

  const handleOnBlur = useCallback(async (event) => {
    let inputValue = event.target.value;
    if (patients.filter(item => item.document_number === inputValue).length <= 1) {
      await searchPatient.getPatient(inputValue, documentType);
    } else {
      message.error({ content: 'Error. El paciente ya existe en esta pre admisión', style: { marginTop: '45vh' } });
      form.resetFields();
      form.setFieldsValue({ 'documentType': documentType });
    }
  }, [patients, searchPatient, documentType, form]);

  const processedPatientRef = useRef(null);
  const initialJobPositionResolvedRef = useRef(false);

  const resolveJobPositionId = useCallback((professionName, currentJobPositionId) => {
    if (currentJobPositionId) return currentJobPositionId;
    if (!professionName || !jobPositionsList?.length) return null;

    const match = jobPositionsList.find(
      (jobPosition) => jobPosition.name.toLowerCase() === professionName.toLowerCase()
    );
    return match ? match.id : professionName;
  }, [jobPositionsList]);

  useEffect(() => {
    if (!searchPatient.data || processedPatientRef.current === searchPatient.data) return;
    processedPatientRef.current = searchPatient.data;
    initialJobPositionResolvedRef.current = true;

    const patientProfessionName = searchPatient.data.position || null;
    const patientJobPositionId = resolveJobPositionId(patientProfessionName, searchPatient.data.job_position_id);

    updatePatientAttribute(index, 'profession', patientProfessionName);
    updatePatientAttribute(index, 'job_position_id', patientJobPositionId);
    updatePatientAttribute(index, 'area_id', searchPatient.data.area_id);
    updatePatientAttribute(index, 'names', searchPatient.data.names);
    updatePatientAttribute(index, 'last_name', searchPatient.data.last_name);
    updatePatientAttribute(index, 'email', searchPatient.data.email);
    updatePatientAttribute(index, 'phone', searchPatient.data.phone);
    updatePatientAttribute(index, 'birthdate', searchPatient.data.birthdate);
    updatePatientAttribute(index, 'gender', (searchPatient.data.gender !== null && searchPatient.data.gender !== undefined) ? parseInt(searchPatient.data.gender) : null);
  }, [searchPatient.data, form, index, updatePatientAttribute, resolveJobPositionId]);

  useEffect(() => {
    if (initialJobPositionResolvedRef.current) return;
    if (!patient || !jobPositionsList?.length) return;

    if (patient.job_position_id) {
      initialJobPositionResolvedRef.current = true;
      return;
    }

    const patientProfessionName = patient.profession || null;
    if (!patientProfessionName) return;

    initialJobPositionResolvedRef.current = true;
    const patientJobPositionId = resolveJobPositionId(patientProfessionName, null);

    updatePatientAttribute(index, 'job_position_id', patientJobPositionId);
    updatePatientAttribute(index, 'profession', patientProfessionName);
  }, [patient, jobPositionsList, index, updatePatientAttribute, resolveJobPositionId]);

  const lastEvaluatedRef = useRef({
    gender: null,
    birthdate: null,
    evaluation_type: null,
    job_position_id: null,
  });

  useEffect(() => {
    if (!patient) {
      return;
    }

    const currentGender = patient.gender;
    const currentBirthdate = patient.birthdate;
    const currentEvaluationType = patient.evaluation_type;
    const currentJobPositionId = patient.job_position_id;

    const hasChanged =
      currentGender !== lastEvaluatedRef.current.gender ||
      currentBirthdate !== lastEvaluatedRef.current.birthdate ||
      currentEvaluationType !== lastEvaluatedRef.current.evaluation_type ||
      currentJobPositionId !== lastEvaluatedRef.current.job_position_id;

    if (hasChanged) {
      lastEvaluatedRef.current = {
        gender: currentGender,
        birthdate: currentBirthdate,
        evaluation_type: currentEvaluationType,
        job_position_id: currentJobPositionId,
      };

      if (patient.prestations && patient.prestations.length > 0) {
        const runReevaluation = async () => {
          const currentPatientData = {
            ...patient,
            ...patient?.generalData,
          };

          const refactorServices = [];
          for (const serviceItem of patient.prestations) {
            let resolvedServiceItem = { ...serviceItem };

            if (serviceItem.type === 'Collection') {
              try {
                const response = await getServices({
                  query: serviceItem.name,
                  limit: 10,
                  branch_office_id: reservationForm.getFieldValue('branch_office_id'),
                  company_id: reservationForm.getFieldValue('company_id'),
                  outsourcing_id: reservationForm.getFieldValue('outsourcing_id'),
                  third_party_id: reservationForm.getFieldValue('third_company_id'),
                  evaluation_type: currentEvaluationType,
                });

                const matchingService = response?.data?.find(service => service.id === serviceItem.id && service.type === 'Collection');
                if (matchingService) {
                  resolvedServiceItem = {
                    ...serviceItem,
                    name: matchingService.name,
                    description: matchingService.description || serviceItem.description,
                    sale_amount: matchingService.sale_amount,
                    original_sale_amount: matchingService.sale_amount,
                    currency_id: matchingService.currency_id || serviceItem.currency_id,
                    department_id: matchingService.department_id || serviceItem.department_id,
                    battery_exams: matchingService.battery_exams || [],
                    report_type: matchingService.report_type || null,
                    medical_form_type: matchingService.medical_form_type || null,
                    observations: matchingService.observations || null,
                  };
                }
              } catch (error) {
                console.error('Error fetching updated collection for new evaluation type:', error);
                message.error(`Error actualizando tipo de evaluación para ${serviceItem.name}`);
              }
            }

            let removedExamIds = [];
            if (resolvedServiceItem.battery_exams && resolvedServiceItem.battery_exams.length > 0) {
              try {
                setLoadingPatient((_loading) => ({ ..._loading, evaluationType: true }));
                const evaluationResult = await evaluateConditionals({
                  patientData: currentPatientData,
                  batteryExams: resolvedServiceItem.battery_exams,
                  selectedKeywords: []
                });
                removedExamIds = evaluationResult.removedExamIds || [];
              } catch (error) {
                console.error('Error evaluating conditionals on patient/type change:', error);
                message.error('Error evaluando exámenes condicionales');
              } finally {
                setLoadingPatient((_loading) => ({ ..._loading, evaluationType: false }));
              }
            }

            const originalSaleAmount = resolvedServiceItem.original_sale_amount !== undefined ? resolvedServiceItem.original_sale_amount : (resolvedServiceItem.net_amount !== undefined ? resolvedServiceItem.net_amount : resolvedServiceItem.sale_amount);
            let calculatedSaleAmount = originalSaleAmount;

            if ((String(resolvedServiceItem.type).toLowerCase().includes('battery') || String(resolvedServiceItem.type).toLowerCase().includes('bateria') || String(resolvedServiceItem.type).toLowerCase().includes('collection') || String(resolvedServiceItem.type).toLowerCase().includes('protocol')) && resolvedServiceItem.battery_exams && resolvedServiceItem.battery_exams.length > 0) {
              let baseExamsMissingPrice = false;
              let baseExamsTotal = 0;
              let removedExamsTotal = 0;

              const activeExams = resolvedServiceItem.battery_exams.filter(exam => !removedExamIds.includes(exam.id));
              const removedExams = resolvedServiceItem.battery_exams.filter(exam => removedExamIds.includes(exam.id));

              for (const exam of activeExams) {
                if (exam.sale_amount === null || exam.sale_amount === undefined || exam.sale_amount === '') {
                  baseExamsMissingPrice = true;
                } else {
                  const price = exam.net_amount !== undefined ? parseFloat(exam.net_amount) : parseFloat(exam.sale_amount);
                  baseExamsTotal += price || 0;
                }
              }

              for (const exam of removedExams) {
                if (exam.sale_amount != null && exam.sale_amount !== '') {
                  const price = exam.net_amount !== undefined ? parseFloat(exam.net_amount) : parseFloat(exam.sale_amount);
                  removedExamsTotal += price || 0;
                }
              }

              calculatedSaleAmount = baseExamsMissingPrice ? ((parseFloat(originalSaleAmount) || 0) - removedExamsTotal) : baseExamsTotal;
            }

            refactorServices.push({
              ...resolvedServiceItem,
              sale_amount: calculatedSaleAmount,
              removedExamIds: removedExamIds,
            });
          }

          updatePatientServices(index, 'prestations', refactorServices);
        };

        runReevaluation();
      }
    }
  }, [
    patient?.gender,
    patient?.birthdate,
    patient?.evaluation_type,
    patient?.job_position_id,
    patient?.prestations?.length,
    index,
    updatePatientServices
  ]);

  useEffect(() => {
    const triggerValidation = () => {
      form.validateFields().catch(() => { });
    };
    window.addEventListener('submitPatientFormPE', triggerValidation);
    return () => window.removeEventListener('submitPatientFormPE', triggerValidation);
  }, [form]);

  const onFieldsChange = useCallback((values) => {
    try {
      if (values[0].name[0] !== 'prestations') {
        if (values[0].name[0] === 'block_id') {
          const filterBlockName = blocks.find(block => block.id === values[0].value);
          updatePatientAttribute(index, values[0].name[0], values[0].value);
          updatePatientAttribute(index, 'block', filterBlockName['block']);
        } else if (values[0].name[0] === 'birthdate') {
          updatePatientAttribute(index, 'birthdate', values[0].value ? values[0].value.format('YYYY-MM-DD') : null);
        } else if (values[0].name[0] === 'job_position_id') {
          updatePatientAttribute(index, 'job_position_id', values[0].value);
          const selectedPosition = jobPositionsList?.find(jobPosition => jobPosition.id === values[0].value);
          updatePatientAttribute(index, 'profession', values[0].item?.name || selectedPosition?.name || null);
        } else {
          updatePatientAttribute(index, values[0].name[0], values[0].value);
        }
      } else if (extraServiceData && extraServiceData.length > 0) {
        const processServices = async () => {
          let refactorServices = [];

          const currentPatientData = {
            ...patients[index],
            ...patients[index]?.generalData,
          };

          for (const item of extraServiceData) {
            if (item?.value) {
              const id = item.value.split('-')[0];

              let removedExamIds = [];
              if (item.battery_exams && item.battery_exams.length > 0) {
                try {
                  setLoadingPatient((_loading) => ({ ..._loading, evaluationType: true }));
                  const evalResult = await evaluateConditionals({
                    patientData: currentPatientData,
                    batteryExams: item.battery_exams,
                    selectedKeywords: []
                  });
                  removedExamIds = evalResult.removedExamIds || [];
                } catch (err) {
                  console.error('Error evaluating conditionals on change:', err);
                  message.error('Error evaluando exámenes condicionales');
                } finally {
                  setLoadingPatient((_loading) => ({ ..._loading, evaluationType: false }));
                }
              }

              let originalSaleAmount = item.original_sale_amount !== undefined ? item.original_sale_amount : (item.net_amount !== undefined ? item.net_amount : item.sale_amount);
              let calculatedSaleAmount = originalSaleAmount;

              if ((String(item.type).toLowerCase().includes('battery') || String(item.type).toLowerCase().includes('bateria') || String(item.type).toLowerCase().includes('collection') || String(item.type).toLowerCase().includes('protocol')) && item.battery_exams && item.battery_exams.length > 0) {
                let baseExamsMissingPrice = false;
                let baseExamsTotal = 0;
                let removedExamsTotal = 0;

                const activeExams = item.battery_exams.filter(e => !removedExamIds.includes(e.id));
                const removedExams = item.battery_exams.filter(e => removedExamIds.includes(e.id));

                for (const exam of activeExams) {
                  if (exam.sale_amount === null || exam.sale_amount === undefined || exam.sale_amount === '') {
                    baseExamsMissingPrice = true;
                  } else {
                    const price = exam.net_amount !== undefined ? parseFloat(exam.net_amount) : parseFloat(exam.sale_amount);
                    baseExamsTotal += price || 0;
                  }
                }

                for (const exam of removedExams) {
                  if (exam.sale_amount != null && exam.sale_amount !== '') {
                    const price = exam.net_amount !== undefined ? parseFloat(exam.net_amount) : parseFloat(exam.sale_amount);
                    removedExamsTotal += price || 0;
                  }
                }

                calculatedSaleAmount = baseExamsMissingPrice ? ((parseFloat(originalSaleAmount) || 0) - removedExamsTotal) : baseExamsTotal;
              }

              refactorServices.push({
                'id': id && parseInt(id, 10),
                'name': item.name || item.children,
                'type': item.type,
                'sale_amount': calculatedSaleAmount,
                'original_sale_amount': originalSaleAmount,
                'currency_id': item.currency_id,
                'department_id': item.department_id,
                'description': item.description,
                'battery_exams': item.battery_exams || [],
                'removedExamIds': removedExamIds,
                'report_type': item.report_type || null,
                'medical_form_type': item.medical_form_type || null,
                'observations': item.observations || null,
              });
              if (item.attached_services?.length > 0 && countrySettings.addAttachedServices["PE"]) {
                item.attached_services.forEach(attached => {
                  if (!refactorServices.some(service => service.id === attached.id && service.type === attached.type)) {
                    refactorServices.push({
                      'id': attached.id,
                      'name': attached.name,
                      'type': attached.type,
                      'sale_amount': attached.sale_amount,
                      'description': attached.description,
                      'department_id': attached.department_id,
                      'battery_exams': attached.battery_exams || [],
                      'removedExamIds': [],
                      'report_type': attached.report_type || null,
                      'medical_form_type': attached.medical_form_type || null,
                    });
                  }
                });
              }
            }
          }
          updatePatientServices(index, values[0].name[0], refactorServices);
          setExtraServiceData([]);
        };

        processServices();
      }
    } catch (error) {
      console.error(error);
      message.error('Ha ocurrido un error al actualizar los datos del paciente');
    }
  }, [extraServiceData, blocks, updatePatientAttribute, index, jobPositionsList, patients, updatePatientServices, countrySettings.addAttachedServices]);

  const fetchServices = useMemo(() => debounce(async (name) => {
    setLoadingServices(true);
    try {
      const response = hasEnoughSearchLength(name)
        ? await getServices({
          query: name,
          limit: 50,
          branch_office_id: reservationForm.getFieldValue('branch_office_id'),
          company_id: reservationForm.getFieldValue('company_id'),
          outsourcing_id: reservationForm.getFieldValue('outsourcing_id'),
          third_party_id: reservationForm.getFieldValue('third_company_id'),
          evaluation_type: form.getFieldValue('evaluation_type')
        })
        : { data: [] };
      setServices(_services => [
        ...(response.data?.filter((foundService) => !patient.prestations.some((_service) => _service.id === foundService.id)) || []),
        ...(patient?.prestations || []),
      ]);
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingServices(false);
    }
  }, PRESTATIONS_SEARCH_DEBOUNCE_MS), [reservationForm]);

  const fetchProjects = useMemo(() => debounce(async (name) => {
    setLoadingProjects(true);
    try {
      const response = await getProjects({ query: name, company_id: reservationForm.getFieldValue('company_id') });
      setProjectsList(response);
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingProjects(false);
    }
  }, 500), [reservationForm]);

  const fetchedProjectsRef = useRef(new Set());

  useEffect(() => {
    const fetchInitialProject = async () => {
      const projectId = patient?.project_id;
      if (projectId && !fetchedProjectsRef.current.has(Number(projectId))) {
        try {
          const [project] = await getProject({ id: projectId });
          if (project) {
            fetchedProjectsRef.current.add(Number(projectId));
            setProjectsList(prev => [...prev, project]);
          }
        } catch (error) {
          console.error('Error fetching initial project:', error);
        }
      }
    };
    fetchInitialProject();
  }, [patient?.project_id]);

  const handleOnChangeSearch = useCallback((field, value, option) => {
    if (value) {
      form.setFieldsValue({ [field]: value });
      setExtraServiceData(option);
    } else {
      form.setFieldsValue({ [field]: null });
      setExtraServiceData([]);
      setServices([]);
    }
  }, [form]);

  const handleCancel = useCallback(() => {
    overCapacityForm.resetFields();
    setIsOverCapacityModalVisible(false);
    updatePatientAttribute(index, 'block_id', null);
    updatePatientAttribute(index, 'block', null);
    setCurrentBlock(null);
  }, [overCapacityForm, index, updatePatientAttribute]);

  const fetchBlocks = useCallback(async () => {
    setLoading(true);
    try {
      const reservationDate = reservationForm.getFieldValue('reservation_date');
      const branchOfficeId = reservationForm.getFieldValue('branch_office_id');
      const departmentId = getCookie('department_id');
      if (departmentId && reservationDate && branchOfficeId) {
        const response = await getBlocks({
          branchOffice: branchOfficeId,
          date: reservationDate.format('YYYY-MM-DD'),
          department: departmentId,
        });
        setBlocks(response.blocks);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [reservationForm]);

  const capacityByBlock = useCallback((block) => {
    if (!block) return 0;
    return block.max_capacity - map(patients, 'block_id').filter(blockId => blockId === block.id).length;
  }, [patients]);

  const handleRepeatBlocksForAll = useCallback(() => {
    const withoutOrDifferentBlocks = patients.filter(p => (!p.block || p.block_id !== patients[0].block_id)).length;
    if (capacityByBlock(currentBlock) >= withoutOrDifferentBlocks) {
      repeatBlocksForAllPatients();
    } else {
      message.info({
        content: t('reservations.patientForm.repeatBlockForAllDisclaimer'),
        style: { marginTop: '45vh' },
        duration: 5,
      });
    }
  }, [patients, capacityByBlock, currentBlock, repeatBlocksForAllPatients, t]);

  const handleBlock = useCallback((event, option) => {
    setCurrentBlock(option.block);
    if (capacityByBlock(option.block) < 0) {
      overCapacityForm.resetFields();
      setIsOverCapacityModalVisible(true);
    }
  }, [capacityByBlock, overCapacityForm]);

  const submitOverCapacity = useCallback(async (values) => {
    setOverCapacityLoading(true);
    try {
      await overcapacityCheck({ code: values.code });
      overCapacityForm.resetFields();
      setIsOverCapacityModalVisible(false);
      setCurrentBlock(null);
      updatePatientAttribute(index, 'over_capacity', true);
      message.success(t('reservations.patientForm.overcapacityRequestSuccessMessage'));
    } catch (error) {
      console.error(error);
      message.error(t('reservations.patientForm.overcapacityRequestErrorMessage'));
      handleCancel();
    } finally {
      setOverCapacityLoading(false);
    }
  }, [t, handleCancel, overCapacityForm, index, updatePatientAttribute]);

  return {
    t,
    form,
    overCapacityForm,
    loading,
    overCapacityLoading,
    loadingServices,
    loadingProjects,
    isOverCapacityModalVisible,
    services,
    blocks,
    documentType,
    genders,
    isProfessionEditable,
    currentEvaluationType,
    setDocumentType,
    handleOnBlur,
    onFieldsChange,
    fetchServices,
    fetchProjects,
    projects: projectsList,
    handleOnChangeSearch,
    handleCancel,
    fetchBlocks,
    handleRepeatBlocksForAll,
    handleBlock,
    submitOverCapacity,
    capacityByBlock,
    searchPatient,
    setServices,
    currentBlock,
    loadingPatient,
  };
};
